import { useEffect, useState } from "react";
import { IconSparkles, IconRobot, IconLock } from "@tabler/icons-react";
import styles from "../TalentProfile.module.css";
import { mapToScoreComparisonData, ScoreComparisonBars } from "./ScoreComparison";

export function mapToAiSummaryData(talentDetails = {}) {
  const submitted = !!talentDetails?.submittedPsychometricTest;
  const hidden = !talentDetails?.psychometricTestResult;
  const insights = talentDetails?.psychometricTestResult ?? {};

  return {
    hidden,
    submitted,
    summary: insights.summary ?? "",
    strengths: insights.strengths ?? [],
    weaknesses: insights.weaknesses ?? [],
    roleFit: insights.roleFit ?? [],
    scores: submitted && !hidden ? mapToScoreComparisonData(talentDetails) : [],
  };
}

function TextSections({ summary, strengths, weaknesses, roleFit }) {
  return (
    <>
      <div className={styles.aiSection}>
        <div className={styles.aiSectionLabel}>Summary:</div>
        <p className={styles.aiSectionText}>{summary}</p>
      </div>
      <div className={styles.aiSection}>
        <div className={styles.aiSectionLabel}>Strengths:</div>
        <p className={styles.aiSectionText}>{strengths?.join(", ")}</p>
      </div>
      <div className={styles.aiSection}>
        <div className={styles.aiSectionLabel}>Weaknesses:</div>
        <p className={styles.aiSectionText}>{weaknesses?.join(", ")}</p>
      </div>
      <div className={styles.aiSection}>
        <div className={styles.aiSectionLabel}>Role-fit:</div>
        <p className={styles.aiSectionText}>{roleFit?.join(", ")}</p>
      </div>
    </>
  );
}

export default function AiSummaryCard({
  hidden,
  data,
  psychometricTestResult
}) {
  const [animated, setAnimated] = useState(false);

  // Bars start at width:0 and animate in shortly after mount — same 120ms
  // delay as the old standalone ScoreComparisonCard used.
  useEffect(() => {
    if (hidden) return;
    const t = setTimeout(() => setAnimated(true), 120);
    return () => clearTimeout(t);
  }, [hidden]);

  return (
    <div className={`${styles.card} ${styles.aiStructCard}`}>
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardIcon}>
            <IconSparkles size={18} />
          </div>
          <div>
            <div className={styles.cardTitle}>AI Summary</div>
            <div className={styles.cardSubtitle}>
              Generated match insight · review before deciding
            </div>
          </div>
        </div>
        {!hidden && (
          <span className={styles.aiBadgePill}>
            <IconRobot size={12} /> AI-generated
          </span>
        )}
      </div>

      <div className={styles.cardBody}>
        {hidden ? (
          <div className={styles.psyHiddenNotice}>
            <div className={styles.psyHiddenIcon}>
              <IconLock size={22} />
            </div>
            <div className={styles.psyHiddenText}>
              <div className={styles.psyHiddenTitle}>
                Psychometric Test Hidden by Talent
              </div>
              <div className={styles.psyHiddenDesc}>
                This talent has chosen to hide their psychometric test results
                and AI-generated summary from their profile. Their professional
                summary and work experience are still available below. You may
                reach out to them directly via the Message button.
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.aiStructBody}>
            <div>
              <TextSections
                summary={psychometricTestResult?.summary}
                strengths={psychometricTestResult?.strengths}
                weaknesses={psychometricTestResult?.weaknesses}
                roleFit={psychometricTestResult?.roleFit}
              />
            </div>
            <div className={styles.aiStructDivider} />
            <div>
              <ScoreComparisonBars scores={mapToScoreComparisonData(data)} animated={animated} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
